import { FormControl, FormHelperText, IconButton, InputBase } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { preventEnterPressed } from 'utility/helpers';
import { styled, useTheme } from '@mui/material/styles';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import ErrorWarningLineIcon from 'remixicon-react/ErrorWarningLineIcon';
import useUserData from 'hooks/useUserData';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { ConfigEntryType } from 'config/config';
import { Asterisk, FieldLabel } from './PostDetailsForm';

type PostTitleProps = {
  autoFocus?: boolean;
  required?: boolean;
  selectedTemplateId: string | undefined;
  field?: ConfigEntryType;
  setPostTitle: Dispatch<SetStateAction<string>>;
};

function PostTitle({
  autoFocus,
  required = true,
  selectedTemplateId,
  field,
  setPostTitle,
}: PostTitleProps) {
  const { companyId } = useUserData();
  const theme = useTheme();
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<PostFormData>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { isLoading } = useConfigQuery({
    companyId,
    configName: CONFIGS.ARTICLE_FIELDS.configName,
    shouldFetch: false,
  });

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.setSelectionRange(-1, -1);
      inputRef.current.focus();
    }
  }, [inputRef, selectedTemplateId, isLoading]);

  return (
    <>
      {field && (
        <FieldLabel id={field.entryCode} disabled={isSubmitting}>
          {field.referenceObject?.label || field.entryDescription}
          {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
        </FieldLabel>
      )}

      <Controller
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <FormControl fullWidth sx={{ mb: 1.5 }}>
            <TitleInput
              {...field}
              inputRef={(_ref) => {
                ref(_ref);
                inputRef.current = _ref;
              }}
              autoFocus={autoFocus}
              multiline
              maxRows={10}
              inputProps={{ minLength: 3 }}
              error={!!fieldState.error}
              onKeyDown={preventEnterPressed}
              fullWidth
              placeholder={`Add a title${required ? '*' : ''}`}
              value={!!field?.value?.trim() ? field.value : ''}
              disabled={isSubmitting}
              onBlur={() => {
                field.onBlur();
                setValue('title', field?.value?.trim(), {
                  shouldDirty: true,
                  shouldValidate: false,
                });

                setPostTitle(field?.value?.trim());
              }}
              endAdornment={
                !!fieldState.error && (
                  <IconButton size="small">
                    <ErrorWarningLineIcon color={theme.palette.custom.redWine} />
                  </IconButton>
                )
              }
              sx={
                !!fieldState.error
                  ? (theme) => ({
                      border: `1px solid ${theme.palette.custom.redWine}`,
                      borderRadius: '5px',
                    })
                  : {}
              }
            />
            {!!fieldState.error && (
              <FormHelperText sx={{ ml: 0 }} error>
                {fieldState.error?.message}
              </FormHelperText>
            )}
          </FormControl>
        )}
        name="title"
      />
    </>
  );
}

const TitleInput = styled(InputBase, { label: 'TitleInput' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  transition: 'background-color 0.3s',
  backgroundColor: theme.palette.custom.whiteSmoke,
  '&&&': {
    padding: '4px 8px',
    borderRadius: 4,
  },
  '&:hover, &:focus-within': {
    backgroundColor: theme.palette.custom.inputHoverBackground,
  },
}));

export default PostTitle;
